/**
 * Sanitize a value to prevent Excel formula injection
 * If the value starts with =, +, -, or @, prefix it with a single quote
 * @param {*} value - The value to sanitize
 * @returns {*} - The sanitized value
 */
const sanitizeForExcel = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  // Convert to string for checking
  const strValue = String(value);

  // Check if the value starts with potentially dangerous characters
  if (strValue.length > 0 && /^[=+\-@]/.test(strValue)) {
    // Prefix with a single quote to prevent formula injection
    return "'" + strValue;
  }

  return value;
};

/**
 * Export SEP Parts data to Excel
 * @param {Array} partsData - Array of part objects from the API response
 * @param {string} strategyNumber - The DMSM strategy number
 */
export const exportSepPartsToExcel = async (partsData, strategyNumber) => {
  if (!partsData || partsData.length === 0) {
    console.warn('No parts data available to export');
    return;
  }

  const XLSX = await import('xlsx-js-style');

  // Sort the data by: startOfProductionFiscalYear (1st), csProgramId (2nd), machineType (3rd), machineNumber (4th), partNumber (5th)
  const sortedPartsData = [...partsData].sort((a, b) => {
    // 1st: Start of Production Fiscal Year
    if (a.startOfProductionFiscalYear !== b.startOfProductionFiscalYear) {
      return (
        (a.startOfProductionFiscalYear || 0) -
        (b.startOfProductionFiscalYear || 0)
      );
    }

    // 2nd: CS Program ID
    if (a.csProgramId !== b.csProgramId) {
      return String(a.csProgramId || '').localeCompare(
        String(b.csProgramId || '')
      );
    }

    // 3rd: Machine Type
    if (a.machineType !== b.machineType) {
      return String(a.machineType || '').localeCompare(
        String(b.machineType || '')
      );
    }

    // 4th: Machine Number
    if (a.machineNumber !== b.machineNumber) {
      return String(a.machineNumber || '').localeCompare(
        String(b.machineNumber || '')
      );
    }

    // 5th: Part Number
    return String(a.partNumber || '').localeCompare(String(b.partNumber || ''));
  });

  // Define the columns we want to export
  const columns = [
    {
      header: 'Start of Production Fiscal Year',
      key: 'startOfProductionFiscalYear'
    },
    { header: 'CS Program ID', key: 'csProgramId' },
    { header: 'Unit Code', key: 'unitCode' },
    { header: 'Machine Type', key: 'machineType' },
    { header: 'Machine Number', key: 'machineNumber' },
    { header: 'Machine Volume', key: 'machineVolume' },
    { header: 'Part Number', key: 'partNumber' },
    { header: 'Material Group Code', key: 'materialGroupCode' },
    { header: 'Supplier Number', key: 'supplierNumber' },
    { header: 'Supplier Name', key: 'supplierName' },
    { header: 'Part Quantity', key: 'partQuantity' },
    { header: 'Piece Price', key: 'piecePrice' },
    { header: 'Extended Cost', key: 'extendedCost' },
    { header: 'DMSM Strategy Number', key: 'dmsmStrategyNumber' },
    { header: 'DMSM Supplier Status', key: 'dmsmSupplierStatus' }
  ];

  // Create worksheet data
  const worksheetData = [];

  // Add header row
  const headerRow = columns.map((col) => col.header);
  worksheetData.push(headerRow);

  // Add data rows
  sortedPartsData.forEach((part) => {
    const row = columns.map((col) => {
      const value = part[col.key];

      // Keep numeric fields as numbers for Excel (not strings)
      if (col.key === 'piecePrice' || col.key === 'extendedCost') {
        if (value === null || value === undefined) {
          return '';
        }
        const numValue = Number(value);
        // Guard against NaN - return empty string if not a valid number
        return Number.isFinite(numValue) ? numValue : '';
      }

      // For numeric fields that should stay as numbers (volumes, quantities, years)
      if (
        col.key === 'machineVolume' ||
        col.key === 'partQuantity' ||
        col.key === 'startOfProductionFiscalYear'
      ) {
        if (value === null || value === undefined) {
          return '';
        }
        const numValue = Number(value);
        return Number.isFinite(numValue) ? numValue : '';
      }

      // For all other fields (strings), sanitize to prevent formula injection
      return sanitizeForExcel(value);
    });
    worksheetData.push(row);
  });

  // Create worksheet from data
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Define styles
  const headerStyle = {
    font: { bold: true, name: 'Verdana', sz: 11, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    fill: {
      patternType: 'solid',
      fgColor: { rgb: '367C2B' },
      bgColor: { rgb: '367C2B' }
    },
    border: {
      top: { style: 'medium', color: { rgb: '808080' } },
      bottom: { style: 'medium', color: { rgb: '808080' } },
      left: { style: 'medium', color: { rgb: '808080' } },
      right: { style: 'medium', color: { rgb: '808080' } }
    }
  };

  const dataStyle = {
    font: { name: 'Verdana', sz: 10 },
    alignment: { wrapText: true }
  };

  const currencyStyle = {
    font: { name: 'Verdana', sz: 10 },
    alignment: { wrapText: true },
    numFmt: '0.00' // Display numbers with 2 decimal places
  };

  // Find column indices for piecePrice and extendedCost
  const piecePriceColIndex = columns.findIndex(
    (col) => col.key === 'piecePrice'
  );
  const extendedCostColIndex = columns.findIndex(
    (col) => col.key === 'extendedCost'
  );

  // Apply styles to header row
  const range = XLSX.utils.decode_range(ws['!ref']);
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!ws[cellAddress]) continue;
    ws[cellAddress].s = headerStyle;
  }

  // Apply styles to data rows
  for (let R = range.s.r + 1; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) continue;
      // Apply currency style to price/cost columns, regular style to others
      if (C === piecePriceColIndex || C === extendedCostColIndex) {
        ws[cellAddress].s = currencyStyle;
      } else {
        ws[cellAddress].s = dataStyle;
      }
    }
  }

  // Set column widths
  ws['!cols'] = [
    { wch: 15 }, // Start of Production Fiscal Year
    { wch: 15 }, // CS Program ID
    { wch: 12 }, // Unit Code
    { wch: 15 }, // Machine Type
    { wch: 20 }, // Machine Number
    { wch: 15 }, // Machine Volume
    { wch: 20 }, // Part Number
    { wch: 20 }, // Material Group Code
    { wch: 18 }, // Supplier Number
    { wch: 40 }, // Supplier Name
    { wch: 15 }, // Part Quantity
    { wch: 15 }, // Piece Price
    { wch: 15 }, // Extended Cost
    { wch: 20 }, // DMSM Strategy Number
    { wch: 20 } // DMSM Supplier Status
  ];

  // Create workbook and add worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'SEP Parts Detail');

  // Write file
  const fileName = `SEP_Parts_Detail_Strategy_${strategyNumber}.xlsx`;
  XLSX.writeFile(wb, fileName);
};
